import type { SynthesizedIntegration } from './types';

/** Source for authenticated (non-public) directory events. */
export const DIRECTORY_ANALYTICS_SOURCE = 'In-App';

/** Integrations whose enabled/disabled state is meaningful enough to always report. */
const CAN_TRACK_STATE = ['Segment.com'];

/** Mixpanel name overrides applied before sending a click event. */
const INTEGRATION_NAME_OVERRIDES: Record<string, string> = {
  'Amazon Redshift': 'Redshift',
  'Amazon S3': 'S3',
};

export function mixpanelIntegrationName(name: string): string {
  return INTEGRATION_NAME_OVERRIDES[name] ?? name;
}

export type IntegrationClickProperties = {
  Source: string;
  'Integration Name': string;
  'Integration Category': string;
  'Integration Type': string;
  'Integration Sub-Category'?: string;
  'Integration Status'?: string;
};

type ClickInput = {
  from: string;
  name: string;
  category?: string;
  type: string;
  subtitle?: string;
  /** `undefined` omits the status (the default for cards we don't track disabled state for). */
  enabled: boolean | undefined;
};

function buildClickProperties(data: ClickInput): IntegrationClickProperties {
  const properties: IntegrationClickProperties = {
    Source: data.from,
    'Integration Name': mixpanelIntegrationName(data.name),
    'Integration Category': data.category ?? '',
    'Integration Type': data.type,
  };
  if (data.subtitle != null && data.subtitle !== '') {
    properties['Integration Sub-Category'] = data.subtitle;
  }
  if (typeof data.enabled === 'boolean') {
    properties['Integration Status'] = data.enabled ? 'Enabled' : 'Disabled';
  }
  return properties;
}

/**
 * Standard card click payload. The `enabled` state is only reported for
 * integrations we can track disabled state for; every other card reports it
 * only when truthy (`!!enabled || undefined`).
 */
export function standardCardClickProperties(
  integration: Pick<SynthesizedIntegration, 'name' | 'category' | 'type' | 'subtitle' | 'enabled'>,
): IntegrationClickProperties {
  const canTrack = CAN_TRACK_STATE.includes(integration.name);
  const enabled = canTrack ? integration.enabled : integration.enabled === true || undefined;
  return buildClickProperties({
    from: DIRECTORY_ANALYTICS_SOURCE,
    name: integration.name,
    category: integration.category,
    type: integration.type,
    subtitle: integration.subtitle,
    enabled,
  });
}

/** Ad-audience card click payload (fixed Advertising category; reports credential presence as status). */
export function audienceCardClickProperties(
  integration: Pick<SynthesizedIntegration, 'name' | 'type'>,
  hasCredential: boolean,
): IntegrationClickProperties {
  return buildClickProperties({
    from: DIRECTORY_ANALYTICS_SOURCE,
    name: integration.name,
    category: 'Advertising',
    type: integration.type,
    enabled: hasCredential,
  });
}

export type IntegrationListProperties = {
  Source: string;
  'List Searched': boolean;
  'List Filtered by Category': boolean;
  'List Filtered by Type': boolean;
  'Integration Category'?: string;
  'Integration Type'?: string;
  'Integration Search Value'?: string;
};

/** Payload for the `list_searched` / `list_filtered` events (same wire event). */
export function listSearchedOrFilteredProperties(input: {
  query: string;
  category: string;
  type: string;
}): IntegrationListProperties {
  const properties: IntegrationListProperties = {
    Source: DIRECTORY_ANALYTICS_SOURCE,
    'List Searched': input.query !== '',
    'List Filtered by Category': input.category !== '',
    'List Filtered by Type': input.type !== '',
  };
  if (input.category !== '') {
    properties['Integration Category'] = input.category;
  }
  if (input.type !== '') {
    properties['Integration Type'] = input.type;
  }
  if (input.query !== '') {
    properties['Integration Search Value'] = input.query;
  }
  return properties;
}
